// src/components/layout/navbar.jsx
import React from "react";
import { HashLink } from "react-router-hash-link";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, X } from "lucide-react";
import { useCart } from "../../Context/Cartcontext";

const NAV_ITEMS = [
  { label: "DIARIES", to: "/Dailyplanner" },
  { label: "PLANNERS", to: "/Productivityplanner" },
  { label: "GIFTS HAMPER", to: "/gift-hampers" },
  { label: "PHOTOBOOKS", to: "/photobook" },
  { label: "JOURNALS", to: "/gratitude-journals" },
  { label: "NOTEBOOKS", to: "/notebooks" },
  { label: "NOTEPADS", to: "/list-notepads" },
  { label: "SKETCHBOOK", to: "/bold-bright" },
  { label: "TABLE CALENDAR", to: "/travel-planner" },
];

const MOBILE_MENU_ITEMS = [
  { label: "Diaries", to: "/Dailyplanner" },
  { label: "Planners", to: "/Productivityplanner" },
  { label: "Gift Hampers", to: "/gift-hampers" },
  { label: "Photobooks", to: "/photobook" },
  { label: "Journals", to: "/gratitude-journals" },
  { label: "Notebooks", to: "/notebooks" },
  { label: "Notepads", to: "/list-notepads" },
  { label: "Sketchbook", to: "/bold-bright" },
  { label: "Table Calendar", to: "/travel-planner" },
];

const Navbar = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const { totalItems } = useCart();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  // Helper to check if a link is active
  const isActive = (to) => {
    if (to.startsWith("/#")) {
      return pathname === "/" && hash === to.replace("/", "");
    }
    return pathname.toLowerCase() === to.toLowerCase();
  };

  const getTabStyle = (to) => {
    const active = isActive(to);
    return `px-2.5 lg:px-3 py-1.5 mx-0.5 lg:mx-1 text-[10.5px] lg:text-[11px] font-bold tracking-widest rounded-md transition-all whitespace-nowrap ${
      active ? "bg-gray-800 text-white shadow-sm" : "text-gray-700 hover:bg-gray-100"
    }`;
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      {/* Desktop Navbar */}
      <nav className="hidden md:flex w-full bg-white border-b border-gray-100 sticky top-16 z-50 h-15">
        <div className="flex items-center justify-center flex-wrap w-full lg:px-2 py-3">
          {NAV_ITEMS.map((item) => (
            <Link key={item.label} to={item.to} className={getTabStyle(item.to)}>
              {item.label}
            </Link>
          ))}

          <HashLink
            smooth
            to="/#corporate-enquiry"
            className={getTabStyle("/#corporate-enquiry")}
          >
            BULK ENQUIRY
          </HashLink>

<button onClick={() => navigate("/cartPage")}
  className="relative ml-3 p-2 rounded-md hover:bg-gray-100 transition-colors"
  aria-label={totalItems > 0 ? `View cart, ${totalItems} items` : "View cart"}>
  <ShoppingBag size={18} />
  {totalItems > 0 && (
    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FFEB3B] text-[9px] font-bold flex items-center justify-center text-black">
      {totalItems}
    </span>
  )}
</button>
        </div>
      </nav>

      {/* Fully Responsive Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-40 transition-opacity" onClick={closeMobileMenu} />
          <div className="fixed top-0 right-0 h-full w-[85vw] max-w-[320px] bg-white z-50 flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out">
            
            {/* Mobile Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0 bg-gray-50/50">
              <span className="font-bold text-xl text-gray-900 tracking-wide" style={{ fontFamily: "'Dancing Script', cursive" }}>
                Aishcraft
              </span>
              <button 
                onClick={closeMobileMenu} 
                className="p-2 text-gray-600 hover:text-black rounded-full hover:bg-gray-200/65 transition-colors"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Navigation Links */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="flex flex-col gap-1.5">
                {MOBILE_MENU_ITEMS.map((item) => (
                  <Link 
                    key={item.label} 
                    to={item.to} 
                    onClick={closeMobileMenu}
                    className={`px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
                      isActive(item.to) 
                        ? "bg-gray-900 text-white shadow-sm" 
                        : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}

                <HashLink 
                  smooth 
                  to="/#corporate-enquiry" 
                  onClick={closeMobileMenu}
                  className={`px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
                    isActive("/#corporate-enquiry") 
                      ? "bg-gray-900 text-white shadow-sm" 
                      : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
                  }`}
                >
                  Bulk Enquiry
                </HashLink>
              </div>
            </div>

            {/* Sticky Bottom Cart Action */}
            <div className="p-4 border-t border-gray-100 bg-white flex-shrink-0 shadow-lg">
              <button 
                onClick={() => { navigate("/cartPage"); closeMobileMenu(); }}
                className="w-full flex items-center justify-center gap-2.5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl py-3.5 text-sm font-bold tracking-wide transition-all shadow-md active:scale-[0.98]"
              >
                <ShoppingBag size={18} /> 
                <span>View Cart</span> 
                {totalItems > 0 && (
                  <span className="ml-1 bg-[#FFEB3B] text-black px-2 py-0.5 rounded-full text-xs font-extrabold">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>

          </div>
        </>
      )}
    </>
  );
};

export default Navbar;